import moment from 'moment';

import {
  ALL_GENDERS_VALUE,
  ALL_GENDERS_LIST,
  FREE_TRIAL_ELIGIBLE,
  FREE_TRIAL_INELIGIBLE,
  FREE_TRIAL_NULL_VALUE,
  UPLOADED_AUDIO,
  EXISTING_VOICEOVER,
  NEW_VOICEOVER,
  BG_MUSIC,
  EXISTING_VO_BG_MUSIC,
  UPLOADED_COVER_ART,
} from 'config';
import {
  createDefaultAudioUploadObj,
  createDefaultImageUploadObj,
} from 'config/uploadProperties';

import { daysBetweenDates } from 'utils/dateHelper';
import { clamp } from 'utilities';

export function newAdFormatter(newAd) {
  const {
    clickthroughUrl,
    dateRange,
    audioSubmission,
    uploads,
    name,
    internalName,
    objective,
  } = newAd;

  const {
    gender,
    platforms,
    genres,
    freeTrial,
    ageRange,
    locations,
    moments,
    fanAudiences,
  } = newAd.targeting;

  const {
    totalBudget,
  } = newAd.payments;

  const selectedGenres = genres.map(genre => genre.id);
  const selectedPlatforms = Object.keys(platforms).filter(k => platforms[k] === true).map(k => k.toLowerCase());
  const selectedLocations = locations.map(loc => loc.id);
  const selectedMoments = moments.map(m => m.id);
  const selectedFanAudiences = fanAudiences.map(fans => ({ artistGid: fans.id, artistName: fans.name }));

  const ad = {
    name,
    banner: uploads.image.uploadInfo.id,
    genders: (gender === ALL_GENDERS_VALUE) ? ALL_GENDERS_LIST : [gender],
    geo: selectedLocations,
    genres: selectedGenres,
    platforms: selectedPlatforms,
    moments: selectedMoments,
    audiences: selectedFanAudiences,
    clickthroughUrl: clickthroughUrl,
    dateBegin: moment(dateRange.begin).format(),
    dateEnd: moment(dateRange.end).format(),
    ageMin: ageRange.start,
    ageMax: ageRange.end,
    totalBudget,
    internalName,
    objective,
  };

  if (freeTrial !== FREE_TRIAL_NULL_VALUE) {
    ad.freeTrialEligible = (freeTrial === FREE_TRIAL_ELIGIBLE).toString();
  }

  switch (audioSubmission.active) {
    case NEW_VOICEOVER:
      if (uploads[BG_MUSIC].uploaded) {
        ad.bgmusicId = uploads[BG_MUSIC].uploadInfo.id;
      }
      if (audioSubmission[NEW_VOICEOVER].bgMusic.id) {
        ad.bgmusicId = audioSubmission[NEW_VOICEOVER].bgMusic.id;
      }

      ad.voiceover = {
        name: audioSubmission[NEW_VOICEOVER].name,
        transcript: audioSubmission[NEW_VOICEOVER].transcript,
        instructions: audioSubmission[NEW_VOICEOVER].instructions,
        locale: audioSubmission[NEW_VOICEOVER].locale,
        voice: audioSubmission[NEW_VOICEOVER].voice,
      };
      break;
    case UPLOADED_AUDIO:
      ad.fullmixId = uploads[UPLOADED_AUDIO].uploadInfo.id;
      break;
    case EXISTING_VOICEOVER:
      ad.voiceover = {
        id: audioSubmission[EXISTING_VOICEOVER].data.id,
      };
      if (uploads[EXISTING_VO_BG_MUSIC].uploaded) {
        ad.bgmusicId = uploads[EXISTING_VO_BG_MUSIC].uploadInfo.id;
      }
      if (audioSubmission[EXISTING_VOICEOVER].bgMusic.id) {
        ad.bgmusicId = audioSubmission[EXISTING_VOICEOVER].bgMusic.id;
      }
      break;
    default:
  }

  return ad;
}

export function platformFill(platforms) {
  return platforms.reduce((platformObj, platform) => {
    platformObj[platform] = true;
    return platformObj;
  }, { android: false, ios: false, desktop: false });
}

export function genderFill(genders) {
  return genders.length === 1 ? genders[0] : ALL_GENDERS_VALUE;
}

export function freeTrialFill(freeTrialEligible) {
  let freeTrial;
  if (freeTrialEligible) {
    freeTrial = FREE_TRIAL_ELIGIBLE;
  } else if (typeof freeTrialEligible === 'boolean') {
    freeTrial = FREE_TRIAL_INELIGIBLE;
  } else {
    freeTrial = FREE_TRIAL_NULL_VALUE;
  }

  return freeTrial;
}

export function getUri(id) {
  return `https://drdxyzdg0sjtu.cloudfront.net/assets/audiocomponent/${id}/asset`;
}

export function editAdFormatter(ad) {
  const {
    bgMusicId = '',
    targeting,
  } = ad;

  const editAd = {
    id: ad.id,
    name: ad.name,
    internalName: ad.internalName,
    targeting: {
      locations: targeting.geo,
      platforms: platformFill(targeting.platforms),
      gender: genderFill(targeting.genders),
      ageRange: {
        start: targeting.ageMin.toString(),
        end: targeting.ageMax.toString(),
      },
      moments: targeting.moments,
      genres: targeting.genres,
      freeTrial: freeTrialFill(targeting.freeTrialEligible),
      fanAudiences: [],
    },
    dateRange: {
      begin: ad.dateBegin,
      end: ad.dateEnd,
    },
    payments: {
      totalBudget: ad.totalBudget,
    },
    uploads: {
      [UPLOADED_COVER_ART]: createDefaultImageUploadObj(),
      [UPLOADED_AUDIO]: createDefaultAudioUploadObj(),
      [BG_MUSIC]: {
        uploadInfo: {
          id: bgMusicId,
          uri: bgMusicId ? getUri(bgMusicId) : '',
        },
      },
      [EXISTING_VO_BG_MUSIC]: createDefaultAudioUploadObj(),
    },
  };

  return editAd;
}

export function audienceSizeFormatter(ad) {
  const {
    targeting: {
      ageRange: {
        start: ageMin,
        end: ageMax,
      },
      gender,
      fanAudiences,
      genres,
      platforms,
      locations,
      moments,
    },
    payments: {
      totalBudget: budget,
    },
    dateRange: {
      begin: dateStart,
      end: dateEnd,
    },
  } = ad;

  const genders = ((gender === ALL_GENDERS_VALUE) ? ALL_GENDERS_LIST : [gender])
    .map(g => g.toLowerCase());
  const artists = fanAudiences.map(fans => fans.id);
  const genresList = genres.map(genre => genre.id);
  const playlists = moments.map(playlist => playlist.id.replace('momenttag:', ''));

  const payload = {
    ageMin: Number(ageMin),
    ageMax: Number(ageMax),
    numDays: daysBetweenDates(dateStart, dateEnd),
    budget: clamp(Number(budget), 250, 5000),
    genders,
    genres: genresList,
    artists,
    platforms: Object.keys(platforms).filter(k => platforms[k] === true).map(k => k),
    playlists,
    geo: locations.map(loc => {
      return typeof loc.id === 'number' ? loc.id : Number(loc.id.replace('dfp:', ''));
    }),
  };

  return payload;
}



// WEBPACK FOOTER //
// ./src/api/formatter.js